import React, { forwardRef, useState } from 'react';
import './TextInput.css';

export interface TextInputProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'style'> {
  /** Show clear button when text is present (default: true). */
  showClearButton?: boolean;
  /** Callback when clear button is pressed. */
  onClear?: () => void;
  /** Show password visibility toggle when type=password (default: true). */
  showSecureToggle?: boolean;
  /** Custom trailing element (overrides clear/secure-toggle). */
  trailingElement?: React.ReactNode;
  /** Style for outer container. */
  containerStyle?: React.CSSProperties;
  /** Style for the glass wrapper. */
  wrapperStyle?: React.CSSProperties;
  /** Style for the input itself. */
  inputStyle?: React.CSSProperties;
  /** Corner radius (default: 20). */
  borderRadius?: number;
  /** Whether the input is in error state. */
  error?: boolean;
  /** Use type=password with a toggle. RN-style alias. */
  secureTextEntry?: boolean;
  /** RN-style alias for onChange. */
  onChangeText?: (value: string) => void;
}

/**
 * Web equivalent of `apps/WunderGuide/common/TextInput.tsx`. Glass dark-purple
 * input matching RN INPUT_CONFIG (rgba(30,15,45,0.85), 1px hairline, radius 20).
 */
const TextInput = forwardRef<HTMLInputElement, TextInputProps>(
  (
    {
      showClearButton = true,
      onClear,
      showSecureToggle = true,
      trailingElement,
      containerStyle,
      wrapperStyle,
      inputStyle,
      borderRadius = 20,
      error,
      secureTextEntry,
      type,
      value,
      onChange,
      onChangeText,
      placeholder,
      className = '',
      ...rest
    },
    ref,
  ) => {
    const [isSecureHidden, setIsSecureHidden] = useState(true);
    const isSecure = secureTextEntry && isSecureHidden;
    const showSecureIcon =
      secureTextEntry && showSecureToggle && !trailingElement;

    const stringValue = typeof value === 'string' ? value : '';
    const hasText = stringValue.length > 0;
    const showClear =
      showClearButton && hasText && !trailingElement && !secureTextEntry;

    const resolvedType = isSecure
      ? 'password'
      : secureTextEntry
        ? 'text'
        : (type ?? 'text');

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      onChange?.(e);
      onChangeText?.(e.target.value);
    };

    const handleClear = () => {
      const fakeEvent = {
        target: { value: '' },
        currentTarget: { value: '' },
      } as React.ChangeEvent<HTMLInputElement>;
      onChange?.(fakeEvent);
      onChangeText?.('');
      onClear?.();
    };

    return (
      <div className={`wg-input ${className}`.trim()} style={containerStyle}>
        <div
          className={`wg-input__wrapper${error ? ' wg-input__wrapper--error' : ''}`}
          style={{ borderRadius, ...wrapperStyle }}
        >
          <div className="wg-input__row">
            <input
              ref={ref}
              className="wg-input__field"
              style={inputStyle}
              type={resolvedType}
              value={value as string | number | readonly string[] | undefined}
              onChange={handleChange}
              placeholder={placeholder}
              {...rest}
            />
            {trailingElement}
            {showSecureIcon && (
              <button
                type="button"
                className="wg-input__icon-btn"
                onClick={() => setIsSecureHidden(s => !s)}
                aria-label={isSecureHidden ? 'Show password' : 'Hide password'}
              >
                {isSecureHidden ? <EyeOffIcon /> : <EyeIcon />}
              </button>
            )}
            {showClear && (
              <button
                type="button"
                className="wg-input__icon-btn"
                onClick={handleClear}
                aria-label="Clear"
              >
                <CloseIcon />
              </button>
            )}
          </div>
        </div>
      </div>
    );
  },
);

TextInput.displayName = 'TextInput';

export default TextInput;
export { TextInput };

/* ---------- inline icons (no extra deps) ---------- */

function EyeIcon() {
  return (
    <svg
      width="22"
      height="22"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}

function EyeOffIcon() {
  return (
    <svg
      width="22"
      height="22"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M17.94 17.94A10.94 10.94 0 0 1 12 20c-7 0-11-8-11-8a18.5 18.5 0 0 1 5.06-5.94" />
      <path d="M9.9 4.24A11 11 0 0 1 12 4c7 0 11 8 11 8a18.6 18.6 0 0 1-2.16 3.19" />
      <path d="M14.12 14.12a3 3 0 1 1-4.24-4.24" />
      <line x1="1" y1="1" x2="23" y2="23" />
    </svg>
  );
}

function CloseIcon() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <circle cx="12" cy="12" r="10" />
      <line x1="9" y1="9" x2="15" y2="15" />
      <line x1="15" y1="9" x2="9" y2="15" />
    </svg>
  );
}
